/*=========================================================================================
  File Name: events.js
  Description: Vuex store for handling events (calendar & table) with shared filters
=========================================================================================*/
import axios from "axios";
import moment from "moment";

const state = {
  viewType: "list",
  event: {},
  filters: {
    companies: [],
    types: [],
    startDate: moment().startOf("month").format("YYYY-MM-DD"),
    endDate: moment().endOf("month").format("YYYY-MM-DD"),
    search: null,
  },

  calendar: {
    events: [],
    focusDate: moment().startOf("month").format("YYYY-MM-DD"),
    isLoading: false,
    calendarViewType: "month",
  },

  table: {
    events: [],
    totalEvents: 0,
    currentPage: 1,
    pageSize: 15,
    isLoading: false,
    sortBy: null,
    descending: false,
  },
};

const actions = {
  async fetchCalendarEvents({ commit, state }) {
    commit("SET_CALENDAR_LOADING", true);
    try {
      const extendedStart = moment(state.filters.startDate)
        .subtract(7, "days")
        .format("YYYY-MM-DD");
      const extendedEnd = moment(state.filters.endDate)
        .add(7, "days")
        .format("YYYY-MM-DD");

      const params = new URLSearchParams();
      params.append("start", extendedStart);
      params.append("end", extendedEnd);
      params.append("paginate", false);
      params.append("useDefaultToyotaCompanies", true);

      if (state.filters.search) params.append("search", state.filters.search);

      state.filters.companies.forEach((id) => params.append("companies", id));
      state.filters.types.forEach((type) => params.append("types", type));

      const response = await axios.get("/api/events", {
        params,
      });
      commit("SET_CALENDAR_EVENTS", response.data.events);
    } finally {
      commit("SET_CALENDAR_LOADING", false);
    }
  },

  async fetchTableEvents({ commit, state }) {
    commit("SET_TABLE_LOADING", true);
    try {
      const params = new URLSearchParams();
      params.append("start", state.filters.startDate);
      params.append("end", state.filters.endDate);
      params.append("page", state.table.currentPage);
      params.append("pageSize", state.table.pageSize);
      params.append("useDefaultToyotaCompanies", true);

      if (state.filters.search) params.append("search", state.filters.search);

      if (state.table.sortBy) {
        params.append("sortBy", state.table.sortBy);
        params.append("descending", state.table.descending);
      }

      state.filters.companies.forEach((id) => params.append("companies", id));
      state.filters.types.forEach((type) => params.append("types", type));
      const response = await axios.get("/api/events", {
        params,
      });

      commit("SET_TABLE_EVENTS", response.data.events);
      commit("SET_TABLE_TOTAL_EVENTS", response.data.total);
    } finally {
      commit("SET_TABLE_LOADING", false);
    }
  },

  async getEvent({ commit }, { id, type }) {
    commit("SET_CALENDAR_LOADING", true);
    try {
      const response = await axios.get(`/api/events/${id}`, {
        params: { type },
      });
      commit("SET_EVENT", response.data);
    } finally {
      commit("SET_CALENDAR_LOADING", false);
    }
  },

  setDateRangeToday({ commit, dispatch }) {
    const today = moment().startOf("month").format("YYYY-MM-DD");
    commit("SET_CALENDAR_FOCUS_DATE", today);
    dispatch("fetchCalendarEvents");
  },

  updateFilters({ commit, dispatch, state }, filters) {
    commit("SET_FILTERS", filters);

    if (state.viewType === "calendar") {
      dispatch("fetchCalendarEvents");
    } else {
      dispatch("fetchTableEvents");
    }
  },

  updateTablePage({ commit, dispatch }, page) {
    commit("SET_TABLE_PAGE", page);
    dispatch("fetchTableEvents");
  },

  updateCalendarFocusDate({ commit }, date) {
    commit("SET_CALENDAR_FOCUS_DATE", date);
  },

  updateDateRange({ commit, dispatch }, { start }) {
    commit("SET_CALENDAR_FOCUS_DATE", start);
    const startDate = moment(start).startOf("month").format("YYYY-MM-DD");
    const endDate = moment(start).endOf("month").format("YYYY-MM-DD");

    commit("SET_FILTER_START_DATE", startDate);
    commit("SET_FILTER_END_DATE", endDate);
    dispatch("fetchCalendarEvents");
  },

  updateFilterDate({ commit, dispatch }, { start, end }) {
    commit("SET_CALENDAR_FOCUS_DATE", start);
    const startDate = moment(start).format("YYYY-MM-DD");
    const endDate = moment(end).format("YYYY-MM-DD");

    commit("SET_FILTER_START_DATE", startDate);
    commit("SET_FILTER_END_DATE", endDate);
    dispatch("fetchTableEvents");
  },

  updateTableSorting({ commit, dispatch }, { sortBy, descending }) {
    commit("SET_TABLE_SORTING", { sortBy, descending });
    commit("SET_TABLE_PAGE", 1);
    dispatch("fetchTableEvents");
  },

  updateSearchString({ commit, dispatch }, search) {
    commit("SET_SEARCH", search);
    dispatch("fetchTableEvents");
  },

  async ensureEventInfo(_, { anchorType, anchorId }) {
    const { data } = await axios.put(`/api/events/${anchorType}/${anchorId}/ensure`);
    return data;
  },

  async updateEventInfo({commit}, { anchorType, anchorId, payload }) {
    const { data } = await axios.put(
      `/api/events/${anchorType}/${anchorId}`,
      payload
    );

    commit("UPSERT_EVENT", data);
  },
};

const mutations = {
  SET_SEARCH(state, search) {
    state.filters.search = search;
  },
  SET_VIEW_TYPE(state, view) {
    state.viewType = view;
  },
  SET_FILTERS(state, { companies, types }) {
    state.filters.companies = companies ?? [];
    state.filters.types = types ?? [];
  },
  SET_FILTER_START_DATE(state, date) {
    state.filters.startDate = date;
  },
  SET_FILTER_END_DATE(state, date) {
    state.filters.endDate = date;
  },

  SET_CALENDAR_LOADING(state, isLoading) {
    state.calendar.isLoading = isLoading;
  },
  SET_CALENDAR_EVENTS(state, events) {
    state.calendar.events = events;
  },
  SET_CALENDAR_FOCUS_DATE(state, date) {
    state.calendar.focusDate = date;
  },
  SET_CALENDAR_VIEW_TYPE(state, viewType) {
    state.calendar.calendarViewType = viewType;
  },
  SET_EVENT(state, event) {
    state.event = event;
  },

  SET_TABLE_LOADING(state, isLoading) {
    state.table.isLoading = isLoading;
  },
  SET_TABLE_EVENTS(state, events) {
    state.table.events = events;
  },
  SET_TABLE_TOTAL_EVENTS(state, total) {
    state.table.totalEvents = total;
  },
  SET_TABLE_PAGE(state, page) {
    state.table.currentPage = page;
  },
  SET_TABLE_PAGE_SIZE(state, size) {
    state.table.pageSize = size;
  },
  SET_TABLE_SORTING(state, { sortBy, descending }) {
    state.table.sortBy = sortBy;
    state.table.descending = descending;
  },
  UPSERT_EVENT(state, updated) {
  const upsertInList = (list) => {
    const i = list.findIndex(
      (e) => e.id === updated.id && e.type === updated.type
    );
    if (i === -1) return [updated, ...list];
    return [
      ...list.slice(0, i),
      updated,
      ...list.slice(i + 1),
    ];
  };

  if (
    state.event &&
    state.event.id === updated.id &&
    state.event.type === updated.type
  ) {
    state.event = updated;
  }

  state.calendar.events = upsertInList(state.calendar.events);
  state.table.events = upsertInList(state.table.events);
},
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
};
